import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import Footer from './Footer/Footer'
import ChatFooter from './Footer/ChatFooter'
import Sidebar from './SideBar/Sidebar'
import AppMobileFooter from './Footer/AppMobileFooter'
import MobileFooter from './MobileFooter'
import { authHandler, refreshTokenHandler } from 'store/actions/auth.action'
import { setChatTheme, setTheme } from 'store/actions/theme.action'
import useIsMobile from 'hooks/useIsMobile'
import { noFooterPages, sideMenuPages } from 'utils/constants'
import HeaderWrapper from './Header/HeaderWrapper'
import { SidebarProvider, useSidebar } from './SideBar/SidebarProvider'

const MarketingLayout = React.memo(({ children }) => {
  const { pathname } = useRouter()
  const isMobile = useIsMobile()

  return (
    <div className="relative w-full h-full">
      {pathname === '/share' ? (
        <div className="app-container">
          {children}
          <MobileFooter />
        </div>
      ) : (
        <>
          <HeaderWrapper isMobile={isMobile} />
          <div className={`app-container ${(pathname === '/' || pathname === '/chat') && '!px-0'}`}>
            {children}
            <MobileFooter />
          </div>
          {<Footer />}
        </>
      )}
    </div>
  )
})

const AppLayout = React.memo(({ children }) => {
  const { pathname } = useRouter()
  const isMobile = useIsMobile()
  const { isAuthenticated } = useSelector((state) => state.auth)
  const { sideMenuVisible } = useSidebar()
  const isFooterVisible = useMemo(() => !noFooterPages.includes(pathname), [pathname])

  const displayLeftMenu = React.useMemo(
    () => isAuthenticated && sideMenuPages.includes(pathname),
    [isAuthenticated, pathname, sideMenuPages]
  )

  return (
    <div className="relative w-full h-full">
      {pathname !== '/share' && <HeaderWrapper isMobile={isMobile} displayLeftMenu={displayLeftMenu} />}
      {!isMobile && displayLeftMenu && <Sidebar sideMenuVisible={sideMenuVisible} />}
      <div
        className={`app-container ${(pathname === '/' || pathname === '/chat') && '!px-0'}`}
        style={{ marginTop: pathname === '/chat' ? 0 : undefined }}
      >
        {isMobile && displayLeftMenu && <AppMobileFooter />}
        <div
          className={`pb-20 md:pb-0 ${
            displayLeftMenu &&
            pathname !== '/share' &&
            `${sideMenuVisible ? 'lg:!pl-[245px] pr-2' : 'md:!pl-[76px] pr-2'}`
          }`}
          style={{ width: '100%' }}
        >
          {children}
        </div>
      </div>
      {!isMobile && isFooterVisible && !displayLeftMenu && <Footer />}
      {isMobile && <MobileFooter />}
    </div>
  )
})

const LayoutContent = React.memo(({ children }) => {
  const dispatch = useDispatch()
  const { pathname } = useRouter()
  const { isAuthenticated, user } = useSelector((state) => state.auth)
  const { activeTheme, activeChatTheme } = useSelector((state) => state.theme)

  useEffect(() => {
    // Authentication handling
    if (!user || isTokenExpired(user?.expiresAt)) {
      dispatch(authHandler())
    }

    // Theme initialization
    if (!activeTheme) {
      const themeValue = localStorage.getItem('main-theme')
      dispatch(setTheme(themeValue))
    }

    if (pathname === '/chat' && activeChatTheme === 'theme5') {
      const chatThemeValue = localStorage.getItem('chat-theme')
      dispatch(setChatTheme(chatThemeValue))
    }
  }, []) // Run only once on mount

  useEffect(() => {
    if (isAuthenticated) {
      const refreshTokenInterval = setInterval(() => {
        dispatch(refreshTokenHandler())
      }, 3600 * 1000)

      return () => clearInterval(refreshTokenInterval)
    }
  }, [isAuthenticated])

  const isTokenExpired = (expiresAt) => {
    if (!expiresAt) return true
    const currentTime = Math.floor(Date.now() / 1000)
    const bufferTime = 60
    return currentTime >= expiresAt - bufferTime
  }

  return (
    <div className={`h-full mx-auto ${pathname !== '/chat' ? activeTheme : activeChatTheme}`}>
      {isAuthenticated ? <AppLayout>{children}</AppLayout> : <MarketingLayout>{children}</MarketingLayout>}
    </div>
  )
})

// Wrap the entire layout with SidebarProvider
const Layout = React.memo(({ children }) => {
  return (
    <SidebarProvider>
      <LayoutContent>{children}</LayoutContent>
    </SidebarProvider>
  )
})

export default Layout
